'use client';

import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import {
  EmailFormSchema,
  FORM_FIELD_NAMES,
  OnboardingFormSchema,
  PROGRAM_STATUS_ENUMS,
  USER_TYPE_ENUMS,
} from '../../../components/Onboarding/types';
import { CLAIMS } from '../../../constants/auth';
import { CCMS_PROGRAM_TYPE_ENUMS } from '../../../constants/programs';
import { TIMEZONE_ENUMS } from '../../../constants/timezones';
import { US_STATE_ENUM_VALS } from '../../../constants/us-states';
import { getSignupData, setSignupData } from '../../../firebase/helpers/onboarding';
import useAppLanguage from '../../../hooks/useAppLanguage';
import useAuth from '../../../hooks/useAuth';
import { BASE_SCHEMA } from '../../../utils/yup';

function useOnboarding() {
  const { currentUser } = useAuth();
  const { translate } = useAppLanguage();

  const oneOfEnum = <T>(enumObject: { [s: string]: T } | ArrayLike<T>) =>
    yup
      .mixed<T>()
      .oneOf(Object.values(enumObject))
      .transform((v) => (!v ? undefined : v));

  const onboardSchema: yup.SchemaOf<OnboardingFormSchema> = useMemo(
    () =>
      yup
        .object({
          [FORM_FIELD_NAMES.PROGRAM_STATUS]: oneOfEnum(PROGRAM_STATUS_ENUMS)
            .default(PROGRAM_STATUS_ENUMS.NEW)
            .required(translate('Program status is required')),
          [FORM_FIELD_NAMES.NAME]: yup
            .string()
            .required(translate('Program name is required'))
            .max(60, translate('Program name cannot exceed 60 characters')),
          [FORM_FIELD_NAMES.NAME_MAX_LENGTH]: yup.number().default(60),
          [FORM_FIELD_NAMES.ADDRESS_1]: yup.string().required(translate('Program address is required')),
          [FORM_FIELD_NAMES.ADDRESS_2]: yup.string().default(''),
          [FORM_FIELD_NAMES.CITY]: yup.string().required(translate('City is required')),
          [FORM_FIELD_NAMES.STATE]: oneOfEnum(US_STATE_ENUM_VALS).required(translate('State is required')),
          [FORM_FIELD_NAMES.ZIPCODE]: yup
            .string()
            .required(translate('Zipcode is required'))
            .matches(/^[0-9]+$/, translate('Must be only digits'))
            .min(5, translate('Must be exactly 5 digits'))
            .max(5, translate('Must be exactly 5 digits')),
          [FORM_FIELD_NAMES.PROGRAM_TYPE]: oneOfEnum(CCMS_PROGRAM_TYPE_ENUMS).required(
            translate('Program type is required')
          ),
          [FORM_FIELD_NAMES.LICENSE_CAPACITY]: yup
            .number()
            .default(1)
            .transform((v) => (!v ? undefined : v))
            .positive(translate('License capacity has to be greater than 0')),
          [FORM_FIELD_NAMES.NETWORK_CODE]: yup.string().default(null),
          [FORM_FIELD_NAMES.PHONE]: yup.string().optional(),
          [FORM_FIELD_NAMES.TIMEZONE]: oneOfEnum(TIMEZONE_ENUMS).required(translate('Timezone is required')),
          [FORM_FIELD_NAMES.LATITUDE]: yup.number().default(0),
          [FORM_FIELD_NAMES.LONGITUDE]: yup.number().default(0),
          [FORM_FIELD_NAMES.FIRST_NAME]: yup.string().default(''),
          [FORM_FIELD_NAMES.LAST_NAME]: yup.string().default(''),
          [FORM_FIELD_NAMES.EMAIL]: yup.string().default(''),
          [FORM_FIELD_NAMES.NEW_SUPPLY]: yup.boolean().default(false),
          [FORM_FIELD_NAMES.LICENSE_NUMBER]: yup.string().default(null),
          ...BASE_SCHEMA,
        })
        .required(),
    [translate]
  );

  const emailSchema: yup.SchemaOf<EmailFormSchema> = useMemo(
    () =>
      yup
        .object({
          [FORM_FIELD_NAMES.EMAIL]: yup
            .string()
            .required(translate('Email is required'))
            .email('Please enter a valid email address'),
          [FORM_FIELD_NAMES.USER_TYPE]: oneOfEnum(USER_TYPE_ENUMS).required().default(null),
        })
        .required(),
    [translate]
  );

  const loadOnboardingData = useCallback(() => {
    if (!currentUser?.uid) {
      console.warn('No current user UID found.'); // Added log
      return null;
    }
    console.log('Loading onboarding data for user UID:', currentUser.uid); // Added log
    return getSignupData(currentUser?.uid);
  }, [currentUser]);

  const addSignupData = useCallback(
    (data: OnboardingFormSchema) => {
      if (!currentUser?.uid) {
        console.warn('No current user UID found when trying to add signup data.'); // Added log
        return null;
      }
      console.log('Adding signup data for user UID:', currentUser.uid, 'Data:', data); // Added log
      return setSignupData(currentUser.uid, data);
    },
    [currentUser]
  );

  const canFillOnboardingForm = useCallback(() => {
    if (!currentUser?.claims) {
      console.warn('No claims found for current user.'); // Added log
      return false;
    }

    const canFill = CLAIMS.ORGANIZATION_ADMIN in currentUser?.claims || CLAIMS.MOXIT_ADMIN in currentUser?.claims;
    console.log('User can fill onboarding form:', canFill); // Added log
    return canFill;
  }, [currentUser]);

  return {
    onboardSchema,
    emailSchema,
    loadOnboardingData,
    addSignupData,
    canFillOnboardingForm,
  };
}

export default useOnboarding;
